import React from 'react';

import {
	AI_MENU_SECTION,
	MAIN_BLOCK_MENU_SECTION_RANK,
	AI_MENU_SECTION_RANK,
} from '@atlaskit/editor-common/block-menu';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { RegisterBlockMenuComponent } from '@atlaskit/editor-plugin-block-menu';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { ToolbarDropdownItemSection } from '@atlaskit/editor-toolbar';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';

import type { AIPlugin } from '../../aiPluginType';
import { countWords } from '../../pm-plugins/ai-monitor-watchmen/utils';
import type { EditorPluginAIProvider } from '../../types/types';

import { getBlockMenuActions } from './actions';
import { BlockMenuActionItem } from './BlockMenuActionItem';
import type { BlockMenuActionDef, BlockMenuSelectionContext } from './types';

/**
 * Builds the selection context from the current selection or the block that triggered the menu.
 *
 * When a preserved selection exists (e.g., the user selected multiple blocks before
 * opening the menu), the context aggregates text and block types across all selected
 * top-level blocks.
 *
 * Otherwise, falls back to the single block at `menuTriggerByNode.pos`.
 */
const getSelectionContext = ({
	api,
	getEditorView,
}: {
	api: ExtractInjectionAPI<AIPlugin>;
	getEditorView: () => EditorView | undefined;
}): BlockMenuSelectionContext | null => {
	const editorView = getEditorView();

	if (!editorView) {
		return null;
	}

	const { state } = editorView;

	if (expValEquals('platform_editor_remix_in_block_menu', 'isEnabled', true)) {
		const textLength = api?.blockControls?.actions.getTextLength(editorView) ?? null;

		if (textLength === null) {
			return null;
		}

		// Derive text content for word-count computation from the same block-controls state.
		const blockControlsState = api?.blockControls?.sharedState.currentState();
		const preservedSelection = blockControlsState?.preservedSelection;
		let textContent: string;

		if (preservedSelection && preservedSelection.from !== preservedSelection.to) {
			textContent = state.doc.textBetween(preservedSelection.from, preservedSelection.to, '\n');
		} else {
			const pos = blockControlsState?.menuTriggerByNode?.pos;
			const node = pos !== null && pos !== undefined ? state.doc.nodeAt(pos) : null;
			textContent = node?.textContent ?? '';
		}

		return {
			textLength,
			wordCount: countWords(textContent),
		};
	}

	// Original inline logic (gate off)
	const blockControlsState = api?.blockControls?.sharedState.currentState();
	const preservedSelection = blockControlsState?.preservedSelection;

	// When a multi-block selection exists, aggregate text metrics across all selected blocks
	if (preservedSelection && preservedSelection.from !== preservedSelection.to) {
		const { from, to } = preservedSelection;
		const textContent = state.doc.textBetween(from, to, '\n');

		return {
			textLength: textContent.length,
			wordCount: countWords(textContent),
		};
	}

	// Single-block fallback: use the node that triggered the menu
	const pos = blockControlsState?.menuTriggerByNode?.pos;
	if (pos === null || pos === undefined) {
		return null;
	}

	const node = state.doc.nodeAt(pos);
	if (!node) {
		return null;
	}

	return {
		textLength: node.textContent.length,
		wordCount: countWords(node.textContent),
	};
};

/**
 * Determines whether an action should appear in the primary menu by
 * evaluating the action's `shouldShowInPrimary` predicate against the
 * current selection context.
 *
 * Falls back to `true` (show in primary) when selection context is unavailable.
 */
const shouldShowInPrimary = ({
	action,
	api,
	getEditorView,
}: {
	action: BlockMenuActionDef;
	api: ExtractInjectionAPI<AIPlugin>;
	getEditorView: () => EditorView | undefined;
}): boolean => {
	const context = getSelectionContext({ api, getEditorView });
	if (!context) {
		return true;
	}
	return action.shouldShowInPrimary(context);
};

export const createAIBlockMenuComponents = ({
	editorPluginAIProvider,
	getEditorView,
	api,
}: {
	api: ExtractInjectionAPI<AIPlugin>;
	editorPluginAIProvider: EditorPluginAIProvider;
	getEditorView: () => EditorView | undefined;
}): RegisterBlockMenuComponent[] => {
	const actions = getBlockMenuActions({
		editorPluginAIProvider,
	});

	return [
		// 1. AI section
		{
			type: 'block-menu-section',
			key: AI_MENU_SECTION.key,
			rank: (MAIN_BLOCK_MENU_SECTION_RANK as Record<string, number>)[AI_MENU_SECTION.key],
			component: ({ children }: { children: React.ReactNode }) => (
				<ToolbarDropdownItemSection hasSeparator={true}>{children}</ToolbarDropdownItemSection>
			),
		},
		// 2. Primary items — all actions, hidden when shouldShowInPrimary returns false
		...actions.map((action, index) => ({
			type: 'block-menu-item' as const,
			key: `ai-${action.key}`,
			parent: {
				type: 'block-menu-section' as const,
				key: AI_MENU_SECTION.key,
				rank: (AI_MENU_SECTION_RANK as Record<string, number>)[AI_MENU_SECTION.key],
			},
			rank: index,
			isHidden: () => !shouldShowInPrimary({ action, api, getEditorView }),
			component: () => (
				<BlockMenuActionItem
					action={action}
					api={api}
					editorPluginAIProvider={editorPluginAIProvider}
					getEditorView={getEditorView}
				/>
			),
		})),
	];
};
